/**
 * ExploreBoard pure helpers (FXC-9009 two-rooms redesign, Phase 2).
 *
 * Everything here is a function of its inputs so the board's gating, axis
 * derivation, evidence lens and click→point matching are unit-testable without
 * mounting plotly/three.
 */
import type { SurfaceResponse as CompositeSurfaceResponse } from "../../../api/composite";
import type { CampaignDetail } from "../../../api/highliftCampaign";
import type { AxisSpec } from "../../adaptive-sampling/types";
import type {
  StudyCursorState,
  StudyEvidenceMode,
  StudySelectedPoint,
} from "../../adaptive-sampling/surfaces/useSliceState";
import type { SweepLiveValues } from "./campaignMath";
import { FILL_AXES, STEP_AXIS_VALUE, type ReplayStep } from "./campaignMath";
import { campaignToAxisSpecs, linspace, reshape } from "./tailorSurfaceAdapter";

/** The fixed card vocabulary of the Explore filmstrip. Slot 0 (geometry) is
 *  locked and unremovable — geometry never leaves the screen (§5.5). */
export type ExploreCardId =
  | "geometry"
  | "cl"
  | "cd"
  | "trust3d"
  | "polar"
  | "cp"
  | "campaign";

export const EXPLORE_CARDS: ReadonlyArray<{ id: ExploreCardId; label: string; locked?: boolean }> = [
  { id: "geometry", label: "geometry", locked: true },
  { id: "cl", label: "CL" },
  { id: "cd", label: "CD" },
  { id: "trust3d", label: "3D trust" },
  { id: "polar", label: "polar" },
  { id: "cp", label: "Cp" },
  { id: "campaign", label: "campaign" },
];

export const EXPLORE_CARD_IDS: ReadonlyArray<ExploreCardId> = EXPLORE_CARDS.map((c) => c.id);

export function isExploreCardId(v: unknown): v is ExploreCardId {
  return typeof v === "string" && (EXPLORE_CARD_IDS as readonly string[]).includes(v);
}

// ── lane layout (Phase 3: promotion gestures — 1-up / 2-up / quad) ──────────
// The hero area holds 1–4 lanes, each showing one card. Click = focus (1-up on
// that card); drag a strip card onto the lanes = split (2-up, then 3, capped
// at quad — the retired free-form TileBoard must not resurrect, §7).

export const MAX_EXPLORE_LANES = 4;

/** Validate an unknown value into a legal lanes array (unique known card ids,
 *  1..4 of them) — used for localStorage rehydration. Null when unusable. */
export function normalizeLanes(value: unknown): ExploreCardId[] | null {
  if (!Array.isArray(value)) return null;
  const lanes: ExploreCardId[] = [];
  for (const v of value) {
    if (isExploreCardId(v) && !lanes.includes(v)) lanes.push(v);
    if (lanes.length === MAX_EXPLORE_LANES) break;
  }
  return lanes.length > 0 ? lanes : null;
}

/** Add a card as a new lane (drag-onto-hero). No-op when already shown or at
 *  the quad cap. */
export function addLane(lanes: readonly ExploreCardId[], id: ExploreCardId): ExploreCardId[] {
  if (lanes.includes(id) || lanes.length >= MAX_EXPLORE_LANES) return [...lanes];
  return [...lanes, id];
}

/** Remove a lane (demote back to the strip) — at least one lane always remains. */
export function removeLane(lanes: readonly ExploreCardId[], id: ExploreCardId): ExploreCardId[] {
  if (lanes.length <= 1) return [...lanes];
  const next = lanes.filter((l) => l !== id);
  return next.length > 0 ? next : [...lanes];
}

/** EXACTLY three named presets + last-used (§5.2 / acceptance 8) — no free-form
 *  dock. Simple is the fresh-profile default; Compare = geometry beside the
 *  surface; Campaign = the dense quad (the brief's convergence/points panels
 *  map onto the campaign live-build + polar cards this vocabulary has). */
export type ExplorePresetId = "simple" | "compare" | "campaign";

export const EXPLORE_PRESETS: ReadonlyArray<{ id: ExplorePresetId; label: string; lanes: ExploreCardId[] }> = [
  { id: "simple", label: "Simple · one surface", lanes: ["cl"] },
  { id: "compare", label: "Compare · geometry + CL", lanes: ["geometry", "cl"] },
  { id: "campaign", label: "Campaign · quad", lanes: ["cl", "trust3d", "campaign", "polar"] },
];

/**
 * The axis metadata the Study Cursor + surface fetches run on. UNGATED from
 * campaigns (§5.2): with a campaign the declared box is authoritative; without
 * one the board falls back to the workbench's canonical operating plane —
 * alpha × deploy ranged (the trust map's axes), every other FILL axis held at
 * its live value. Geometry knob values in runs are UNPREFIXED, and FILL_AXES
 * already speaks the canonical composite_db names.
 */
export function boardAxisSpecs(
  campaign: CampaignDetail | null,
  live: SweepLiveValues,
): AxisSpec[] {
  const campaignAxes = campaign?.config?.axes ?? [];
  if (campaignAxes.length > 0) {
    const specs = campaignToAxisSpecs(campaignAxes);
    if (specs.some((a) => a.mode === "range")) return specs;
  }
  // Ranged pair FIRST and in (α, deploy) order — the default slice picks the
  // first two range axes, and the workbench convention is CL = f(α, deploy)
  // (the trust map's axes), not the FILL_AXES declaration order.
  const rangedNames = ["alpha_deg", "flap_deploy_deg"];
  const byName = new Map(FILL_AXES.map((ax) => [ax.canonical, ax]));
  const ranged = rangedNames.map((name) => {
    const ax = byName.get(name)!;
    return { name, units: ax.label, mode: "range" as const, min: ax.range[0], max: ax.range[1] };
  });
  const held = FILL_AXES.filter((ax) => !rangedNames.includes(ax.canonical)).map((ax) => {
    const value =
      ax.canonical === "flaperon_deflection_deg" ? live.flaperon
      : ax.canonical === "mach" ? live.mach
      : ax.canonical === "re_chord_millions" ? live.reynolds / 1e6
      : ax.canonical === "disk_thrust_loading" ? live.diskLoading
      : 0;
    return { name: ax.canonical, units: ax.label, mode: "fixed" as const, fixedValue: value };
  });
  return [...ranged, ...held];
}

/** Human label for a canonical axis (falls back to the raw name). */
export function axisLabel(name: string): string {
  return FILL_AXES.find((a) => a.canonical === name)?.label ?? name;
}

/** The `fixed` record a /composite/surface fetch pins the slice on: every axis
 *  that is not x/y at its cursor pin (clamped) or its fixed value. */
export function boardFixedValues(
  inputs: readonly AxisSpec[],
  cursor: Pick<StudyCursorState, "xInput" | "yInput" | "pinned">,
): Record<string, number> {
  const fixed: Record<string, number> = {};
  for (const axis of inputs) {
    if (axis.name === cursor.xInput || axis.name === cursor.yInput) continue;
    if (axis.mode === "fixed") {
      if (typeof axis.fixedValue === "number" && Number.isFinite(axis.fixedValue)) {
        fixed[axis.name] = axis.fixedValue;
      }
      continue;
    }
    const lo = axis.min ?? 0;
    const hi = axis.max ?? 0;
    const pin = cursor.pinned[axis.name] ?? 0.5 * (lo + hi);
    fixed[axis.name] = Math.min(hi, Math.max(lo, pin));
  }
  return fixed;
}

/** Build the full N-D input vector for a clicked (x, y) — the buildFullInputs
 *  idiom from SurfacesTab, over the board's axis metadata. */
export function boardFullInputs(
  inputs: readonly AxisSpec[],
  cursor: Pick<StudyCursorState, "xInput" | "yInput" | "pinned">,
  xValue: number,
  yValue: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const axis of inputs) {
    if (axis.mode === "fixed") {
      out[axis.name] = axis.fixedValue ?? 0;
    } else if (axis.name === cursor.xInput) {
      out[axis.name] = xValue;
    } else if (axis.name === cursor.yInput) {
      out[axis.name] = yValue;
    } else {
      out[axis.name] = cursor.pinned[axis.name] ?? 0.5 * ((axis.min ?? 0) + (axis.max ?? 0));
    }
  }
  return out;
}

export type EvidenceGrid = {
  x: number[];
  y: number[];
  z: number[][];
  sigma: number[][];
  /** True when the grid carries NO variation (constant/zero everywhere) —
   *  upstream publishes no signal for this quantity at this slice, so a
   *  heatmap of it would be a misleading uniform wash (review fix: the CD
   *  flat-pink lane; the ROM layer publishes CD=0 — an upstream fact, noted
   *  in the PR, not fixable in this frontend). */
  degenerate: boolean;
};

/** Finite-cell span check for the degeneracy flag. */
function gridDegenerate(z: number[][]): boolean {
  let lo = Infinity;
  let hi = -Infinity;
  let finite = 0;
  for (const row of z) {
    for (const v of row) {
      if (!Number.isFinite(v)) continue;
      finite += 1;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  return finite > 0 && hi - lo < 1e-9;
}

/**
 * The evidence lens over one /composite/surface response (§5.2). ONE control,
 * one aligned semantic across the synced cards (review finding 14):
 *   - corrected (default) — the confidence-weighted FUSED estimate. 2D lanes
 *     show fused.z; 3D lanes show the fused surface; the campaign lane's
 *     points carry the local-model CORRECTION (adbTailorSlice, rejected
 *     points dropped).
 *   - compatible — evidence the fusion actually trusts: the fused grid masked
 *     to confident cells; the campaign lane keeps only transport-compatible
 *     points at their raw values.
 *   - raw — the bare physical evidence, no fusion: the highest-fidelity model
 *     layer (cfd/RANS when present) in 2D, the per-model overlay in 3D, every
 *     solved point as-solved in the campaign lane.
 * Honest: every grid shown is server-fitted; the lens only chooses which.
 */
export function evidenceGrid(
  data: CompositeSurfaceResponse,
  mode: StudyEvidenceMode,
  confidenceFloor = 0.35,
): EvidenceGrid {
  const { nx, ny, x_bounds, y_bounds } = data;
  const x = linspace(x_bounds[0], x_bounds[1], nx);
  const y = linspace(y_bounds[0], y_bounds[1], ny);
  if (mode === "raw") {
    const model =
      data.models.find((m) => m.source_class === "cfd") ??
      data.models.find((m) => m.source_class !== "gp") ??
      data.models[0];
    if (model) {
      const z = reshape(model.z, nx, ny);
      return { x, y, z, sigma: reshape(model.sigma, nx, ny), degenerate: gridDegenerate(z) };
    }
    // No model layers at all — fall through to the fused grid (still honest).
  }
  const z = reshape(data.fused.z, nx, ny);
  const sigma = reshape(data.fused.sigma, nx, ny);
  if (mode === "compatible") {
    for (let j = 0; j < ny; j += 1) {
      for (let i = 0; i < nx; i += 1) {
        const conf = data.fused.confidence[j * nx + i] ?? 0;
        if (conf < confidenceFloor) z[j]![i] = NaN;
      }
    }
  }
  return { x, y, z, sigma, degenerate: gridDegenerate(z) };
}

export type BoardPoint = {
  x: number;
  y: number;
  iter: number;
  source: "sampler" | "manual";
  status: "queued" | "running" | "done";
};

/** Campaign replay steps → overlay points on the current (xInput, yInput) view.
 *  Failed points are dropped (the overlay has no failed glyph); pending never
 *  reach ReplaySteps. */
export function stepsToBoardPoints(
  steps: readonly ReplayStep[],
  xInput: string,
  yInput: string,
): BoardPoint[] {
  const xGet = STEP_AXIS_VALUE[xInput];
  const yGet = STEP_AXIS_VALUE[yInput];
  if (!xGet || !yGet) return [];
  return steps
    .filter((s) => !s.failed)
    .map((s) => ({ x: xGet(s), y: yGet(s), iter: s.iterationIndex, source: "sampler" as const, status: "done" as const }));
}

/** /composite/surface raw samples → overlay points (the no-campaign case). */
export function samplesToBoardPoints(
  data: CompositeSurfaceResponse | undefined,
): BoardPoint[] {
  if (!data) return [];
  const out: BoardPoint[] = [];
  for (const group of data.samples ?? []) {
    for (const p of group.points) {
      out.push({ x: p.x, y: p.y, iter: 0, source: "sampler", status: "done" });
    }
  }
  return out;
}

/** Tailor's proposed queue rows → where-next markers on the current view.
 *  Queue rows speak the workbench's raw vocabulary (alpha_deg, flap_deploy_deg,
 *  mach, reynolds — UNPREFIXED); map the picked axes onto it, skipping axes the
 *  queue doesn't carry. */
export type QueueRowLike = {
  alpha_deg: number;
  flap_deploy_deg: number;
  mach: number;
  reynolds: number;
};

const QUEUE_AXIS_VALUE: Record<string, (row: QueueRowLike) => number> = {
  alpha_deg: (r) => r.alpha_deg,
  flap_deploy_deg: (r) => r.flap_deploy_deg,
  mach: (r) => r.mach,
  re_chord_millions: (r) => r.reynolds / 1e6,
};

export function queueToWhereNext(
  queue: readonly QueueRowLike[],
  xInput: string,
  yInput: string,
): Array<{ x: number; y: number; rank: number }> {
  const xGet = QUEUE_AXIS_VALUE[xInput];
  const yGet = QUEUE_AXIS_VALUE[yInput];
  if (!xGet || !yGet) return [];
  return queue.map((row, i) => ({ x: xGet(row), y: yGet(row), rank: i + 1 }));
}

/**
 * A completed solve → the shared selectedPoint (§5.4 write-back), extracted
 * pure so the field provenance is PINNED (review fix: the inline version built
 * a chimera vector — alpha/deploy from the focused submission but mach/Re from
 * the request point). Every axis reads the FOCUSED submission first and falls
 * back to the request point only when the response omits it; the geometry
 * axes the solve wire doesn't echo come from the live operation.
 */
export function solveFocusToSelectedPoint(
  focus: { run_path?: string | null; alpha_deg?: unknown; flap_deploy_deg?: unknown; mach?: unknown; reynolds?: unknown },
  basePoint: { alpha_deg?: unknown; flap_deploy_deg?: unknown; mach?: unknown; reynolds?: unknown },
  operation: { flaperonAngleDeg: number; diskLoading?: number | null },
): StudySelectedPoint {
  const num = (v: unknown, fallback: number): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    runNodeId: focus.run_path ?? null,
    inputs: {
      alpha_deg: num(focus.alpha_deg, num(basePoint.alpha_deg, 0)),
      flap_deploy_deg: num(focus.flap_deploy_deg, num(basePoint.flap_deploy_deg, 0)),
      mach: num(focus.mach, num(basePoint.mach, 0.2)),
      re_chord_millions: num(focus.reynolds, num(basePoint.reynolds, 0)) / 1e6,
      flaperon_deflection_deg: operation.flaperonAngleDeg,
      disk_thrust_loading: operation.diskLoading ?? 0,
    },
  };
}

/**
 * Click → nearest selectable point. Campaign steps win (they carry a run_path
 * for the geometry re-rig + flow fetch); falls back to the surface's raw
 * samples (position + slice-completed inputs, no run id). Returns null when
 * nothing is within `tolFrac` of the axis ranges (a click on empty canvas
 * selects nothing rather than snapping to a far point).
 */
export function nearestSelectablePoint(
  click: { x: number; y: number },
  opts: {
    steps: readonly ReplayStep[];
    surface: CompositeSurfaceResponse | undefined;
    inputs: readonly AxisSpec[];
    cursor: Pick<StudyCursorState, "xInput" | "yInput" | "pinned">;
    tolFrac?: number;
  },
): StudySelectedPoint | null {
  const { steps, surface, inputs, cursor } = opts;
  const tolFrac = opts.tolFrac ?? 0.05;
  const xBounds = surface?.x_bounds;
  const yBounds = surface?.y_bounds;
  const xSpan = xBounds ? Math.abs(xBounds[1] - xBounds[0]) : 1;
  const ySpan = yBounds ? Math.abs(yBounds[1] - yBounds[0]) : 1;
  const dist = (px: number, py: number): number => {
    const dx = (px - click.x) / (xSpan || 1);
    const dy = (py - click.y) / (ySpan || 1);
    return Math.hypot(dx, dy);
  };

  let best: { d: number; point: StudySelectedPoint } | null = null;
  const xGet = STEP_AXIS_VALUE[cursor.xInput];
  const yGet = STEP_AXIS_VALUE[cursor.yInput];
  if (xGet && yGet) {
    for (const s of steps) {
      if (s.failed) continue;
      const px = xGet(s);
      const py = yGet(s);
      const d = dist(px, py);
      if (best && d >= best.d) continue;
      best = {
        d,
        point: {
          runNodeId: s.runPath,
          inputs: {
            alpha_deg: s.alphaDeg,
            flap_deploy_deg: s.deployDeg,
            flaperon_deflection_deg: s.flaperonDeg,
            mach: s.mach,
            re_chord_millions: s.reMillions,
            disk_thrust_loading: s.blowing,
          },
        },
      };
    }
  }
  if (!best && surface) {
    for (const group of surface.samples ?? []) {
      for (const p of group.points) {
        const d = dist(p.x, p.y);
        if (best && d >= best.d) continue;
        best = {
          d,
          point: { runNodeId: null, inputs: boardFullInputs(inputs, cursor, p.x, p.y) },
        };
      }
    }
  }
  return best && best.d <= tolFrac ? best.point : null;
}
